import {
  Setter,
  TKV2_ORDER_ATTACHMENT_API,
  Tkv2GetCompsResponse,
  Tkv2Property,
  Updater,
  assert,
  expected,
  populated,
} from '@aloft/toolkit-shared';
import { UseQueryOptions, UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import {
  fetchGetTkv2CompsAPI,
  useUpdateTkv2OrderCompAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';

const schQueryData = TKV2_ORDER_ATTACHMENT_API.COMPS.GET.RESPONSE;
type QueryData = z.infer<typeof schQueryData>;

export function useTkv2Comps(
  params: { pathParams: { orderID: string } },
  opts?: UseQueryOptions<Tkv2GetCompsResponse>,
): [UseQueryResult<Tkv2GetCompsResponse>, Record<string, Setter<Tkv2Property>>, boolean] {
  const { fetcherOptions } = useToolkitContext();

  const [dirty, setDirty] = useState<Record<string, Tkv2Property>>({});
  const isDirty = populated(dirty);

  const queryKey = useMemo(
    () => ['order', params.pathParams.orderID, 'comps'],
    [params.pathParams.orderID],
  );
  const queryClient = useQueryClient();

  const getCompQueryData = useCallback(
    (compID: string) => expected((queryClient.getQueryData(queryKey) as QueryData).comps[compID]),
    [queryClient, queryKey],
  );
  const setCompQueryData = useCallback(
    (compID: string, prop: Tkv2Property) => {
      queryClient.setQueryData(queryKey, (qd: QueryData | undefined) => {
        assert(qd !== undefined);
        return { ...qd, comps: { ...qd.comps, [compID]: prop } };
      });
    },
    [queryClient, queryKey],
  );

  const query = useQuery<Tkv2GetCompsResponse>(
    queryKey,
    async () => schQueryData.parse(await fetchGetTkv2CompsAPI({ ...fetcherOptions, ...params })),
    {
      ...opts,
      refetchInterval: isDirty ? false : opts?.refetchInterval,
    },
  );

  const queryData = query.data;

  const shadowedQueryData: QueryData | undefined = shadowQuery(queryData, dirty);

  // @ts-expect-error UseQueryResult has complicated conditional types around
  // whether 'data' is defined or not, and it would be a mess to try to do it
  // in a way that typescript can verify automatically. because
  // shadowQueryData is always defined when data is defined we have nothing to
  // worry about.
  const shadowedQuery: UseQueryResult<QueryData> = {
    ...query,
    data: shadowedQueryData,
  };

  const putDirty = useCallback(
    (id: string, prop: Updater<Tkv2Property>) => {
      setDirty((map) =>
        typeof prop === 'function'
          ? { ...map, [id]: prop(map[id] ?? getCompQueryData(id)) }
          : { ...map, [id]: prop },
      );
    },
    [setDirty, getCompQueryData],
  );
  const clearDirty = useCallback(
    (id: string) =>
      setDirty((map) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: unused, ...rest } = map;
        return rest;
      }),
    [setDirty],
  );

  const mutation = useUpdateTkv2OrderCompAPI({
    onSuccess(data, variables) {
      const { compID } = variables.pathParams;
      setCompQueryData(compID, TKV2_ORDER_ATTACHMENT_API.COMPS.PUT.RESPONSE.parse(data));
    },
  });

  const edgeTrigger = useEdgeTrigger({
    condition: !mutation.isLoading && !query.isInitialLoading && isDirty,
    delayMs: 1000,
    action: () => {
      for (const [compID, comp] of Object.entries(dirty)) {
        mutation.mutate({
          pathParams: { ...params.pathParams, compID },
          body: comp,
        });
        setCompQueryData(compID, comp);
        clearDirty(compID);
      }
    },
  });

  const sequence = useSequenceMemo(queryData?.sequence);
  const setters = useMemo(() => {
    if (sequence === undefined) {
      return {};
    }
    const res: Record<string, Setter<Tkv2Property>> = {};
    for (const compID of sequence) {
      res[compID] = (updater) => {
        queryClient.cancelQueries({ queryKey });
        edgeTrigger.restartTimer();
        putDirty(compID, updater);
      };
    }
    return res;
  }, [sequence, queryClient, queryKey, edgeTrigger, putDirty]);

  return [shadowedQuery, setters, isDirty || mutation.isLoading];
}

/**
 * Like `useMemo()`, but changes are detected by inspecting the value itself
 * rather than a dependency array. If the array is unchanged, then the same
 * reference will be returned.
 */
function useSequenceMemo(seq: string[] | undefined): string[] | undefined {
  const ref = useRef<string[] | undefined>(seq);
  if (
    ref.current === undefined ||
    seq === undefined ||
    ref.current.length !== seq.length ||
    ref.current.some((x, i) => seq[i] !== x)
  ) {
    ref.current = seq;
  }
  return ref.current;
}

function shadowQuery(
  queryData:
    | {
        comps: Record<string, Tkv2Property>;
        sequence: string[];
      }
    | undefined,
  dirty: Record<string, Tkv2Property>,
): QueryData | undefined {
  if (queryData === undefined) return undefined;

  // If there are no changes locally to the comps, do not break the reference
  if (Object.keys(dirty).length < 1 && queryData.comps !== undefined) return queryData;

  // Only break the reference when we need to merge the query data (server state)
  // with the dirty state (client state)
  return { sequence: queryData.sequence, comps: { ...queryData.comps, ...dirty } };
}
